// Portal Insights (22_PortalInsights.jsx) — the usage report and the Quote of the Day admin, in the
// portal's four languages (2026-10-21). The quotes themselves (quoteEn / quoteKo / quoteVi …) are data
// and are never translated here; only the screen's own labels are.
const en = {
  title: 'Portal Insights',
  sub: 'Who is actually using the portal, and the quote everyone sees each morning.',
  tabUsage: 'Usage',
  tabQuotes: 'Quote of the Day',
  durMin: '{{m}}m',
  durHm: '{{h}}h {{m}}m',
  refresh: 'Refresh',
  loading: 'Loading…',
  aud: {
    portal: 'Home page (everyone)',
    developer: 'Sample Tracker (developers)',
    portalShort: 'Home page',
    developerShort: 'Sample Tracker',
    home: 'Home',
    both: 'Both screens',
  },
  status: {
    pending: 'Waiting for review',
    approved: 'Published',
    rejected: 'Rejected',
    all: 'All statuses',
  },
  usage: {
    loadError: 'Could not load portal usage.',
    preset: '{{n}} days',
    tileAccounts: 'Active accounts',
    tileUsed: 'Used the portal',
    tileUnused: 'Never opened it',
    tileVisits: 'Total visits',
    tileTime: 'Total time',
    colUser: 'User',
    colRole: 'Role',
    colVisits: 'Visits',
    colTime: 'Time used',
    colDays: 'Days used',
    colAvg: 'Avg / day',
    colLastSeen: 'Last seen (VN)',
    search: 'Search name, e-mail or role…',
    shown: '{{n}} of {{total}}',
    clearSort: 'Clear sort & filters',
    noAccounts: 'No accounts.',
    never: 'never in this range',
    topPages: 'Most visited screens',
    note: 'A visit is one browser tab; reloading the same tab does not count again. Time is only counted while the tab is actually in front, so a window left open overnight is not reported as usage. Days are Vietnam calendar days.',
    csvUser: 'User',
    csvName: 'Name',
    csvEmail: 'Email',
    csvRole: 'Role',
    csvVisits: 'Visits',
    csvMinutes: 'Active minutes',
    csvDays: 'Days used',
    csvAvg: 'Avg min/day',
    csvLastSeen: 'Last seen (VN)',
  },
  quotes: {
    source: 'Source: {{note}}',
    modelFallback: 'model',
    publish: 'Publish',
    reject: 'Reject',
    edit: 'Edit',
    delete: 'Delete',
    loadError: 'Could not load the quotes.',
    updateError: 'Could not update the quote.',
    deleteConfirm: 'Delete this quote by {{author}}?',
    deleteError: 'Could not delete the quote.',
    genAdded: '{{n}} new quote(s) added for review.',
    genSkipped: '{{n}} already in the pool.',
    genDropped: '{{n}} dropped as unverifiable.',
    genModel: 'Model: {{model}}.',
    genFailed: 'Generation failed.',
    aiHead: 'New quotes are written by Claude and wait here for your approval',
    aiBody: 'A model can produce a sentence nobody actually said, or get a birth year wrong, so nothing reaches the home page until you publish it. Each row shows where the quote comes from and how certain the model was.',
    shortLead: 'Not enough quotes to keep the {{days}}-day no-repeat rule.',
    shortItem: '{{screen}}: {{n}} approved = {{n}} days',
    shortTail: 'After that the oldest one comes round again — publish more, or generate a batch.',
    noKeyLead: 'ANTHROPIC_API_KEY is not set on the server.',
    noKeyRest: 'No new quotes can be generated, and the pool is already too small.',
    curatedLead: 'Quotes are curated by hand.',
    curatedRest: '{{n}} published, enough to cover the {{days}}-day no-repeat rule. AI generation is off.',
    addKey: 'Add the key to {{path}} and restart the backend if you want new quotes written automatically.',
    generating: 'Generating…',
    generateNow: 'Generate now',
    modelLabel: 'model: {{model}}',
    publishedHome: 'Published — home',
    publishedDev: 'Published — developers',
    add: 'Add a quote',
    colStatus: 'Status',
    colQuote: 'Quote',
    colAttribution: 'Attribution',
    colShownOn: 'Shown on',
    colTimes: 'Times',
    noMatch: 'No quotes match this filter.',
    recent: 'Recently shown',
  },
  form: {
    required: 'The quote text and the author are both required.',
    saveError: 'Could not save the quote.',
    editTitle: 'Edit quote #{{id}}',
    addTitle: 'Add a quote',
    close: 'Close',
    quoteEn: 'Quote — English *',
    quoteVi: 'Quote — Tiếng Việt',
    quoteZh: 'Quote — 简体中文',
    quoteFr: 'Quote — Français',
    quoteJa: 'Quote — 日本語',
    quoteKo: 'Quote — 한국어',
    author: 'Author *',
    authorKo: 'Author — 한국어',
    authorZh: 'Author — 简体中文',
    authorFr: 'Author — Français',
    authorJa: 'Author — 日本語',
    roleEn: 'Role / what they did',
    roleVi: 'Role — Tiếng Việt',
    roleKo: 'Role — 한국어',
    roleZh: 'Role — 简体中文',
    roleFr: 'Role — Français',
    roleJa: 'Role — 日本語',
    org: 'Company / organisation',
    orgVi: 'Organisation — Tiếng Việt',
    orgZh: 'Organisation — 简体中文',
    orgFr: 'Organisation — Français',
    orgJa: 'Organisation — 日本語',
    orgViPh: 'blank for a company name',
    orgKo: 'Organisation — 한국어',
    birth: 'Birth year',
    death: 'Death year (blank if living)',
    shownOn: 'Shown on',
    theme: 'Theme',
    source: 'Source / how certain the attribution is',
    sourcePh: "Interview, book, or 'widely attributed'",
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
  },
};

const ko = {
  title: '포털 인사이트',
  sub: '포털을 실제로 누가 사용하는지, 그리고 매일 아침 모두가 보는 한마디.',
  tabUsage: '사용 현황',
  tabQuotes: '오늘의 한마디',
  durMin: '{{m}}분',
  durHm: '{{h}}시간 {{m}}분',
  refresh: '새로고침',
  loading: '불러오는 중…',
  aud: {
    portal: '홈 화면 (전체)',
    developer: '샘플 트래커 (개발자)',
    portalShort: '홈 화면',
    developerShort: '샘플 트래커',
    home: '홈',
    both: '두 화면 모두',
  },
  status: {
    pending: '검토 대기',
    approved: '게시됨',
    rejected: '반려됨',
    all: '모든 상태',
  },
  usage: {
    loadError: '포털 사용 현황을 불러오지 못했습니다.',
    preset: '{{n}}일',
    tileAccounts: '활성 계정',
    tileUsed: '포털 사용',
    tileUnused: '한 번도 열지 않음',
    tileVisits: '총 방문',
    tileTime: '총 사용 시간',
    colUser: '사용자',
    colRole: '역할',
    colVisits: '방문',
    colTime: '사용 시간',
    colDays: '사용 일수',
    colAvg: '일평균',
    colLastSeen: '마지막 접속 (VN)',
    search: '이름, 이메일, 역할 검색…',
    shown: '{{total}}명 중 {{n}}명',
    clearSort: '정렬·필터 초기화',
    noAccounts: '계정이 없습니다.',
    never: '이 기간에 접속 없음',
    topPages: '가장 많이 본 화면',
    note: '방문은 브라우저 탭 하나를 뜻하며, 같은 탭을 새로고침해도 다시 세지 않습니다. 시간은 탭이 실제로 앞에 떠 있는 동안만 집계하므로, 밤새 열어 둔 창은 사용으로 잡히지 않습니다. 날짜는 베트남 기준 달력일입니다.',
    csvUser: '사용자',
    csvName: '이름',
    csvEmail: '이메일',
    csvRole: '역할',
    csvVisits: '방문',
    csvMinutes: '사용 시간(분)',
    csvDays: '사용 일수',
    csvAvg: '일평균(분)',
    csvLastSeen: '마지막 접속 (VN)',
  },
  quotes: {
    source: '출처: {{note}}',
    modelFallback: '모델',
    publish: '게시',
    reject: '반려',
    edit: '편집',
    delete: '삭제',
    loadError: '명언 목록을 불러오지 못했습니다.',
    updateError: '명언을 업데이트하지 못했습니다.',
    deleteConfirm: '{{author}}의 이 명언을 삭제할까요?',
    deleteError: '명언을 삭제하지 못했습니다.',
    genAdded: '새 명언 {{n}}개가 검토 대기로 추가되었습니다.',
    genSkipped: '{{n}}개는 이미 목록에 있습니다.',
    genDropped: '{{n}}개는 확인할 수 없어 제외했습니다.',
    genModel: '모델: {{model}}.',
    genFailed: '생성에 실패했습니다.',
    aiHead: '새 명언은 Claude가 작성하며, 여기서 승인을 기다립니다',
    aiBody: '모델은 아무도 하지 않은 말을 만들어 내거나 출생 연도를 틀릴 수 있으므로, 게시하기 전까지는 홈 화면에 나가지 않습니다. 각 행에는 명언의 출처와 모델이 얼마나 확신하는지가 표시됩니다.',
    shortLead: '{{days}}일 중복 금지 규칙을 지키기에 명언이 부족합니다.',
    shortItem: '{{screen}}: 승인 {{n}}개 = {{n}}일',
    shortTail: '그 이후에는 가장 오래된 명언이 다시 나옵니다 — 더 게시하거나 한 번에 생성하세요.',
    noKeyLead: '서버에 ANTHROPIC_API_KEY가 설정되어 있지 않습니다.',
    noKeyRest: '새 명언을 생성할 수 없으며, 목록도 이미 부족합니다.',
    curatedLead: '명언은 직접 선별해 관리합니다.',
    curatedRest: '{{n}}개가 게시되어 {{days}}일 중복 금지 규칙을 충족합니다. AI 생성은 꺼져 있습니다.',
    addKey: '새 명언을 자동으로 작성하려면 {{path}}에 키를 추가하고 백엔드를 다시 시작하세요.',
    generating: '생성 중…',
    generateNow: '지금 생성',
    modelLabel: '모델: {{model}}',
    publishedHome: '게시됨 — 홈',
    publishedDev: '게시됨 — 개발자',
    add: '명언 추가',
    colStatus: '상태',
    colQuote: '명언',
    colAttribution: '출처',
    colShownOn: '표시 화면',
    colTimes: '횟수',
    noMatch: '이 필터에 맞는 명언이 없습니다.',
    recent: '최근 표시됨',
  },
  form: {
    required: '명언 내용과 인물은 모두 필수입니다.',
    saveError: '명언을 저장하지 못했습니다.',
    editTitle: '명언 #{{id}} 편집',
    addTitle: '명언 추가',
    close: '닫기',
    quoteEn: '명언 — 영어 *',
    quoteVi: '명언 — Tiếng Việt',
    quoteZh: '명언 — 简体中文',
    quoteFr: '명언 — Français',
    quoteJa: '명언 — 日本語',
    quoteKo: '명언 — 한국어',
    author: '인물 *',
    authorKo: '인물 — 한국어',
    authorZh: '인물 — 简体中文',
    authorFr: '인물 — Français',
    authorJa: '인물 — 日本語',
    roleEn: '역할 / 한 일',
    roleVi: '역할 — Tiếng Việt',
    roleKo: '역할 — 한국어',
    roleZh: '역할 — 简体中文',
    roleFr: '역할 — Français',
    roleJa: '역할 — 日本語',
    org: '회사 / 소속',
    orgVi: '소속 — Tiếng Việt',
    orgZh: '소속 — 简体中文',
    orgFr: '소속 — Français',
    orgJa: '소속 — 日本語',
    orgViPh: '회사명이면 비워 두세요',
    orgKo: '소속 — 한국어',
    birth: '출생 연도',
    death: '사망 연도 (생존 시 비움)',
    shownOn: '표시 화면',
    theme: '주제',
    source: '출처 / 인용의 확실성',
    sourcePh: "인터뷰, 책 또는 '널리 알려진 인용'",
    cancel: '취소',
    save: '저장',
    saving: '저장 중…',
  },
};

const vi = {
  title: 'Thống kê Portal',
  sub: 'Ai thực sự đang dùng portal, và câu nói mọi người thấy mỗi sáng.',
  tabUsage: 'Mức sử dụng',
  tabQuotes: 'Câu nói hôm nay',
  durMin: '{{m}} phút',
  durHm: '{{h}} giờ {{m}} phút',
  refresh: 'Làm mới',
  loading: 'Đang tải…',
  aud: {
    portal: 'Trang chủ (mọi người)',
    developer: 'Theo dõi mẫu (developer)',
    portalShort: 'Trang chủ',
    developerShort: 'Theo dõi mẫu',
    home: 'Trang chủ',
    both: 'Cả hai màn hình',
  },
  status: {
    pending: 'Chờ duyệt',
    approved: 'Đã đăng',
    rejected: 'Bị từ chối',
    all: 'Mọi trạng thái',
  },
  usage: {
    loadError: 'Không tải được dữ liệu sử dụng portal.',
    preset: '{{n}} ngày',
    tileAccounts: 'Tài khoản đang hoạt động',
    tileUsed: 'Đã dùng portal',
    tileUnused: 'Chưa từng mở',
    tileVisits: 'Tổng lượt truy cập',
    tileTime: 'Tổng thời gian',
    colUser: 'Người dùng',
    colRole: 'Vai trò',
    colVisits: 'Lượt truy cập',
    colTime: 'Thời gian dùng',
    colDays: 'Số ngày dùng',
    colAvg: 'TB / ngày',
    colLastSeen: 'Lần cuối (VN)',
    search: 'Tìm tên, email hoặc vai trò…',
    shown: '{{n}} / {{total}}',
    clearSort: 'Bỏ sắp xếp & lọc',
    noAccounts: 'Không có tài khoản.',
    never: 'không truy cập trong khoảng này',
    topPages: 'Màn hình được xem nhiều nhất',
    note: 'Một lượt truy cập là một tab trình duyệt; tải lại cùng tab không được tính thêm. Thời gian chỉ được tính khi tab thực sự đang ở phía trước, nên cửa sổ để mở qua đêm không bị tính là sử dụng. Ngày tính theo lịch Việt Nam.',
    csvUser: 'Người dùng',
    csvName: 'Tên',
    csvEmail: 'Email',
    csvRole: 'Vai trò',
    csvVisits: 'Lượt truy cập',
    csvMinutes: 'Số phút hoạt động',
    csvDays: 'Số ngày dùng',
    csvAvg: 'TB phút/ngày',
    csvLastSeen: 'Lần cuối (VN)',
  },
  quotes: {
    source: 'Nguồn: {{note}}',
    modelFallback: 'mô hình',
    publish: 'Đăng',
    reject: 'Từ chối',
    edit: 'Sửa',
    delete: 'Xóa',
    loadError: 'Không tải được danh sách câu nói.',
    updateError: 'Không cập nhật được câu nói.',
    deleteConfirm: 'Xóa câu nói này của {{author}}?',
    deleteError: 'Không xóa được câu nói.',
    genAdded: 'Đã thêm {{n}} câu nói mới để duyệt.',
    genSkipped: '{{n}} câu đã có sẵn trong danh sách.',
    genDropped: '{{n}} câu bị loại vì không xác minh được.',
    genModel: 'Mô hình: {{model}}.',
    genFailed: 'Tạo câu nói thất bại.',
    aiHead: 'Câu nói mới do Claude viết và chờ bạn duyệt tại đây',
    aiBody: 'Mô hình có thể tạo ra một câu chưa ai từng nói, hoặc ghi sai năm sinh, nên không câu nào lên trang chủ cho đến khi bạn đăng. Mỗi dòng cho biết câu nói lấy từ đâu và mô hình chắc chắn đến mức nào.',
    shortLead: 'Không đủ câu nói để giữ quy tắc không lặp lại trong {{days}} ngày.',
    shortItem: '{{screen}}: {{n}} câu đã duyệt = {{n}} ngày',
    shortTail: 'Sau đó câu cũ nhất sẽ quay lại — hãy đăng thêm hoặc tạo một loạt mới.',
    noKeyLead: 'Máy chủ chưa đặt ANTHROPIC_API_KEY.',
    noKeyRest: 'Không thể tạo câu nói mới, và danh sách đã quá ít.',
    curatedLead: 'Câu nói được chọn thủ công.',
    curatedRest: 'Đã đăng {{n}} câu, đủ cho quy tắc không lặp lại trong {{days}} ngày. Tính năng tạo bằng AI đang tắt.',
    addKey: 'Thêm khóa vào {{path}} và khởi động lại backend nếu bạn muốn câu nói mới được viết tự động.',
    generating: 'Đang tạo…',
    generateNow: 'Tạo ngay',
    modelLabel: 'mô hình: {{model}}',
    publishedHome: 'Đã đăng — trang chủ',
    publishedDev: 'Đã đăng — developer',
    add: 'Thêm câu nói',
    colStatus: 'Trạng thái',
    colQuote: 'Câu nói',
    colAttribution: 'Xuất xứ',
    colShownOn: 'Hiển thị ở',
    colTimes: 'Số lần',
    noMatch: 'Không có câu nói nào khớp bộ lọc này.',
    recent: 'Đã hiển thị gần đây',
  },
  form: {
    required: 'Nội dung câu nói và tác giả đều bắt buộc.',
    saveError: 'Không lưu được câu nói.',
    editTitle: 'Sửa câu nói #{{id}}',
    addTitle: 'Thêm câu nói',
    close: 'Đóng',
    quoteEn: 'Câu nói — Tiếng Anh *',
    quoteVi: 'Câu nói — Tiếng Việt',
    quoteZh: 'Câu nói — 简体中文',
    quoteFr: 'Câu nói — Français',
    quoteJa: 'Câu nói — 日本語',
    quoteKo: 'Câu nói — 한국어',
    author: 'Tác giả *',
    authorKo: 'Tác giả — 한국어',
    authorZh: 'Tác giả — 简体中文',
    authorFr: 'Tác giả — Français',
    authorJa: 'Tác giả — 日本語',
    roleEn: 'Vai trò / việc họ đã làm',
    roleVi: 'Vai trò — Tiếng Việt',
    roleKo: 'Vai trò — 한국어',
    roleZh: 'Vai trò — 简体中文',
    roleFr: 'Vai trò — Français',
    roleJa: 'Vai trò — 日本語',
    org: 'Công ty / tổ chức',
    orgVi: 'Tổ chức — Tiếng Việt',
    orgZh: 'Tổ chức — 简体中文',
    orgFr: 'Tổ chức — Français',
    orgJa: 'Tổ chức — 日本語',
    orgViPh: 'để trống nếu là tên công ty',
    orgKo: 'Tổ chức — 한국어',
    birth: 'Năm sinh',
    death: 'Năm mất (để trống nếu còn sống)',
    shownOn: 'Hiển thị ở',
    theme: 'Chủ đề',
    source: 'Nguồn / mức độ chắc chắn của xuất xứ',
    sourcePh: "Phỏng vấn, sách, hoặc 'được cho là của'",
    cancel: 'Hủy',
    save: 'Lưu',
    saving: 'Đang lưu…',
  },
};

const zh = {
  title: 'Portal 使用分析',
  sub: '谁在真正使用门户，以及大家每天早上看到的那句话。',
  tabUsage: '使用情况',
  tabQuotes: '每日一句',
  durMin: '{{m}}分',
  durHm: '{{h}}小时{{m}}分',
  refresh: '刷新',
  loading: '加载中…',
  aud: {
    portal: '首页（所有人）',
    developer: '样品跟踪（开发员）',
    portalShort: '首页',
    developerShort: '样品跟踪',
    home: '首页',
    both: '两个页面',
  },
  status: {
    pending: '待审核',
    approved: '已发布',
    rejected: '已驳回',
    all: '全部状态',
  },
  usage: {
    loadError: '无法加载门户使用情况。',
    preset: '{{n}} 天',
    tileAccounts: '活跃账户',
    tileUsed: '使用过门户',
    tileUnused: '从未打开',
    tileVisits: '总访问次数',
    tileTime: '总使用时长',
    colUser: '用户',
    colRole: '角色',
    colVisits: '访问次数',
    colTime: '使用时长',
    colDays: '使用天数',
    colAvg: '日均',
    colLastSeen: '最近访问（VN）',
    search: '搜索姓名、邮箱或角色…',
    shown: '{{n}} / {{total}}',
    clearSort: '清除排序和筛选',
    noAccounts: '没有账户。',
    never: '此期间未访问',
    topPages: '访问最多的页面',
    note: '一次访问指一个浏览器标签页；刷新同一标签页不会重复计算。仅在标签页实际处于前台时计时，因此整夜开着的窗口不会被算作使用。日期按越南日历日计算。',
    csvUser: '用户',
    csvName: '姓名',
    csvEmail: '邮箱',
    csvRole: '角色',
    csvVisits: '访问次数',
    csvMinutes: '活跃分钟数',
    csvDays: '使用天数',
    csvAvg: '日均分钟数',
    csvLastSeen: '最近访问（VN）',
  },
  quotes: {
    source: '出处：{{note}}',
    modelFallback: '模型',
    publish: '发布',
    reject: '驳回',
    edit: '编辑',
    delete: '删除',
    loadError: '无法加载名言列表。',
    updateError: '无法更新名言。',
    deleteConfirm: '删除 {{author}} 的这句名言？',
    deleteError: '无法删除名言。',
    genAdded: '已新增 {{n}} 句名言待审核。',
    genSkipped: '{{n}} 句已在库中。',
    genDropped: '{{n}} 句因无法核实被舍弃。',
    genModel: '模型：{{model}}。',
    genFailed: '生成失败。',
    aiHead: '新名言由 Claude 撰写，在此等待您的审批',
    aiBody: '模型可能生成一句从未有人说过的话，或写错出生年份，因此在您发布之前，任何名言都不会出现在首页。每一行都显示名言的出处以及模型的把握程度。',
    shortLead: '名言数量不足以维持 {{days}} 天不重复规则。',
    shortItem: '{{screen}}：已批准 {{n}} 句 = {{n}} 天',
    shortTail: '之后最早的一句会再次出现 — 请发布更多，或批量生成。',
    noKeyLead: '服务器未设置 ANTHROPIC_API_KEY。',
    noKeyRest: '无法生成新名言，且名言库已经不足。',
    curatedLead: '名言由人工精选。',
    curatedRest: '已发布 {{n}} 句，足以满足 {{days}} 天不重复规则。AI 生成已关闭。',
    addKey: '如需自动撰写新名言，请将密钥添加到 {{path}} 并重启后端。',
    generating: '生成中…',
    generateNow: '立即生成',
    modelLabel: '模型：{{model}}',
    publishedHome: '已发布 — 首页',
    publishedDev: '已发布 — 开发员',
    add: '添加名言',
    colStatus: '状态',
    colQuote: '名言',
    colAttribution: '出处',
    colShownOn: '显示位置',
    colTimes: '次数',
    noMatch: '没有符合此筛选的名言。',
    recent: '最近显示',
  },
  form: {
    required: '名言内容和作者均为必填。',
    saveError: '无法保存名言。',
    editTitle: '编辑名言 #{{id}}',
    addTitle: '添加名言',
    close: '关闭',
    quoteEn: '名言 — 英文 *',
    quoteVi: '名言 — Tiếng Việt',
    quoteZh: '名言 — 简体中文',
    quoteFr: '名言 — Français',
    quoteJa: '名言 — 日本語',
    quoteKo: '名言 — 한국어',
    author: '作者 *',
    authorKo: '作者 — 한국어',
    authorZh: '作者 — 简体中文',
    authorFr: '作者 — Français',
    authorJa: '作者 — 日本語',
    roleEn: '身份 / 主要成就',
    roleVi: '身份 — Tiếng Việt',
    roleKo: '身份 — 한국어',
    roleZh: '身份 — 简体中文',
    roleFr: '身份 — Français',
    roleJa: '身份 — 日本語',
    org: '公司 / 机构',
    orgVi: '机构 — Tiếng Việt',
    orgZh: '机构 — 简体中文',
    orgFr: '机构 — Français',
    orgJa: '机构 — 日本語',
    orgViPh: '公司名称请留空',
    orgKo: '机构 — 한국어',
    birth: '出生年份',
    death: '逝世年份（在世请留空）',
    shownOn: '显示位置',
    theme: '主题',
    source: '出处 / 引述的可靠程度',
    sourcePh: "采访、书籍，或“广泛认为出自”",
    cancel: '取消',
    save: '保存',
    saving: '保存中…',
  },
};

// French and Japanese (task #1173, 2026-11-04) — generated from `en`; a string not yet translated reads English.
const fr = {
  "title": "Statistiques du portail",
  "sub": "Qui utilise réellement le portail, et la citation que tout le monde voit chaque matin.",
  "tabUsage": "Utilisation",
  "tabQuotes": "Citation du jour",
  "durMin": "{{m}}m",
  "durHm": "{{h}}h {{m}}m",
  "refresh": "Actualiser",
  "loading": "Chargement…",
  "aud": {
    "portal": "Page d’accueil (tout le monde)",
    "developer": "Sample Tracker (développeurs)",
    "portalShort": "Page d’accueil",
    "developerShort": "Suivi des échantillons",
    "home": "Accueil",
    "both": "Les deux écrans"
  },
  "status": {
    "pending": "En attente de relecture",
    "approved": "Publié",
    "rejected": "Rejeté",
    "all": "Tous les statuts"
  },
  "usage": {
    "loadError": "Impossible de charger l’utilisation du portail.",
    "preset": "{{n}} jours",
    "tileAccounts": "Comptes actifs",
    "tileUsed": "Ont utilisé le portail",
    "tileUnused": "Ne l’ont jamais ouvert",
    "tileVisits": "Visites au total",
    "tileTime": "Temps total",
    "colUser": "Utilisateur",
    "colRole": "Rôle",
    "colVisits": "Visites",
    "colTime": "Temps d’utilisation",
    "colDays": "Jours d’utilisation",
    "colAvg": "Moy. / jour",
    "colLastSeen": "Dernière connexion (VN)",
    "search": "Rechercher un nom, un e-mail ou un rôle…",
    "shown": "{{n}} sur {{total}}",
    "clearSort": "Effacer tris et filtres",
    "noAccounts": "Aucun compte.",
    "never": "jamais sur cette période",
    "topPages": "Écrans les plus visités",
    "note": "Une visite correspond à un onglet du navigateur ; recharger le même onglet ne compte pas une nouvelle fois. Le temps n’est compté que lorsque l’onglet est réellement au premier plan : une fenêtre laissée ouverte toute la nuit n’est donc pas comptée comme de l’utilisation. Les jours sont des jours calendaires du Vietnam.",
    "csvUser": "Utilisateur",
    "csvName": "Nom",
    "csvEmail": "E-mail",
    "csvRole": "Rôle",
    "csvVisits": "Visites",
    "csvMinutes": "Minutes actives",
    "csvDays": "Jours d’utilisation",
    "csvAvg": "Moy. min/jour",
    "csvLastSeen": "Dernière connexion (VN)"
  },
  "quotes": {
    "source": "Source : {{note}}",
    "modelFallback": "modèle",
    "publish": "Publier",
    "reject": "Rejeter",
    "edit": "Modifier",
    "delete": "Supprimer",
    "loadError": "Impossible de charger les citations.",
    "updateError": "Impossible de mettre à jour la citation.",
    "deleteConfirm": "Supprimer cette citation de {{author}} ?",
    "deleteError": "Impossible de supprimer la citation.",
    "genAdded": "{{n}} nouvelle(s) citation(s) ajoutée(s) pour relecture.",
    "genSkipped": "{{n}} déjà dans le stock.",
    "genDropped": "{{n}} écartée(s) car invérifiable(s).",
    "genModel": "Modèle : {{model}}.",
    "genFailed": "Échec de la génération.",
    "aiHead": "Les nouvelles citations sont rédigées par Claude et attendent ici votre approbation",
    "aiBody": "Un modèle peut produire une phrase que personne n’a jamais dite, ou se tromper sur une année de naissance : rien n’atteint donc la page d’accueil tant que vous ne l’avez pas publié. Chaque ligne indique l’origine de la citation et le degré de certitude du modèle.",
    "shortLead": "Pas assez de citations pour respecter la règle de non-répétition sur {{days}} jours.",
    "shortItem": "{{screen}} : {{n}} approuvée(s) = {{n}} jours",
    "shortTail": "Ensuite, la plus ancienne revient — publiez-en davantage ou générez un lot.",
    "noKeyLead": "ANTHROPIC_API_KEY n’est pas définie sur le serveur.",
    "noKeyRest": "Aucune nouvelle citation ne peut être générée, et le stock est déjà trop réduit.",
    "curatedLead": "Les citations sont sélectionnées à la main.",
    "curatedRest": "{{n}} publiées, ce qui suffit pour la règle de non-répétition sur {{days}} jours. La génération par IA est désactivée.",
    "addKey": "Ajoutez la clé dans {{path}} et redémarrez le backend si vous souhaitez que de nouvelles citations soient rédigées automatiquement.",
    "generating": "Génération…",
    "generateNow": "Générer maintenant",
    "modelLabel": "modèle : {{model}}",
    "publishedHome": "Publiées — accueil",
    "publishedDev": "Publiées — développeurs",
    "add": "Ajouter une citation",
    "colStatus": "Statut",
    "colQuote": "Citation",
    "colAttribution": "Attribution",
    "colShownOn": "Affichée sur",
    "colTimes": "Fois",
    "noMatch": "Aucune citation ne correspond à ce filtre.",
    "recent": "Affichées récemment"
  },
  "form": {
    "required": "Le texte de la citation et l’auteur sont tous deux obligatoires.",
    "saveError": "Impossible d’enregistrer la citation.",
    "editTitle": "Modifier la citation n° {{id}}",
    "addTitle": "Ajouter une citation",
    "close": "Fermer",
    "quoteEn": "Citation — English *",
    "quoteVi": "Citation — Tiếng Việt",
    "quoteZh": "Citation — 简体中文",
    "quoteFr": "Citation — Français",
    "quoteJa": "Citation — 日本語",
    "quoteKo": "Citation — 한국어",
    "author": "Auteur *",
    "authorKo": "Auteur — 한국어",
    "authorZh": "Auteur — 简体中文",
    "authorFr": "Auteur — Français",
    "authorJa": "Auteur — 日本語",
    "roleEn": "Rôle / ce qu’il ou elle a fait",
    "roleVi": "Rôle — Tiếng Việt",
    "roleKo": "Rôle — 한국어",
    "roleZh": "Rôle — 简体中文",
    "roleFr": "Rôle — Français",
    "roleJa": "Rôle — 日本語",
    "org": "Société / organisation",
    "orgVi": "Organisation — Tiếng Việt",
    "orgZh": "Organisation — 简体中文",
    "orgFr": "Organisation — Français",
    "orgJa": "Organisation — 日本語",
    "orgViPh": "vide pour un nom de société",
    "orgKo": "Organisation — 한국어",
    "birth": "Année de naissance",
    "death": "Année de décès (vide si vivant)",
    "shownOn": "Affichée sur",
    "theme": "Thème",
    "source": "Source / degré de certitude de l’attribution",
    "sourcePh": "Interview, livre ou « largement attribuée »",
    "cancel": "Annuler",
    "save": "Enregistrer",
    "saving": "Enregistrement…"
  }
};
const ja = {
  "title": "ポータル統計",
  "sub": "ポータルを実際に使っているのは誰か、そして毎朝全員に表示される言葉。",
  "tabUsage": "利用状況",
  "tabQuotes": "今日の一言",
  "durMin": "{{m}}分",
  "durHm": "{{h}}時間{{m}}分",
  "refresh": "更新",
  "loading": "読み込み中…",
  "aud": {
    "portal": "ホームページ（全員）",
    "developer": "Sample Tracker（開発担当）",
    "portalShort": "ホームページ",
    "developerShort": "サンプルトラッカー",
    "home": "ホーム",
    "both": "両方の画面"
  },
  "status": {
    "pending": "確認待ち",
    "approved": "公開済み",
    "rejected": "却下",
    "all": "すべてのステータス"
  },
  "usage": {
    "loadError": "ポータルの利用状況を読み込めませんでした。",
    "preset": "{{n}}日間",
    "tileAccounts": "アクティブなアカウント",
    "tileUsed": "ポータルを利用した人",
    "tileUnused": "一度も開いていない人",
    "tileVisits": "総訪問数",
    "tileTime": "総利用時間",
    "colUser": "ユーザー",
    "colRole": "ロール",
    "colVisits": "訪問数",
    "colTime": "利用時間",
    "colDays": "利用日数",
    "colAvg": "1日平均",
    "colLastSeen": "最終アクセス（VN）",
    "search": "名前、メールアドレス、ロールで検索…",
    "shown": "{{total}}件中{{n}}件",
    "clearSort": "並べ替えとフィルターをクリア",
    "noAccounts": "アカウントがありません。",
    "never": "この期間はアクセスなし",
    "topPages": "よく開かれた画面",
    "note": "訪問はブラウザーのタブ単位で数え、同じタブを再読み込みしても加算されません。時間はタブが実際に前面に表示されている間だけ計測するため、一晩開いたままのウィンドウは利用として扱われません。日数はベトナムの暦日です。",
    "csvUser": "ユーザー",
    "csvName": "名前",
    "csvEmail": "メール",
    "csvRole": "ロール",
    "csvVisits": "訪問数",
    "csvMinutes": "アクティブ時間（分）",
    "csvDays": "利用日数",
    "csvAvg": "1日平均（分）",
    "csvLastSeen": "最終アクセス（VN）"
  },
  "quotes": {
    "source": "出典：{{note}}",
    "modelFallback": "モデル",
    "publish": "公開",
    "reject": "却下",
    "edit": "編集",
    "delete": "削除",
    "loadError": "名言を読み込めませんでした。",
    "updateError": "名言を更新できませんでした。",
    "deleteConfirm": "{{author}}のこの名言を削除しますか？",
    "deleteError": "名言を削除できませんでした。",
    "genAdded": "新しい名言{{n}}件を確認待ちに追加しました。",
    "genSkipped": "{{n}}件はすでに登録済みです。",
    "genDropped": "{{n}}件は裏付けが取れないため除外しました。",
    "genModel": "モデル：{{model}}。",
    "genFailed": "生成に失敗しました。",
    "aiHead": "新しい名言はClaudeが作成し、ここで承認を待ちます",
    "aiBody": "モデルは、誰も言っていない文章を作ったり、生年を間違えたりすることがあります。そのため、公開するまでホームページには何も表示されません。各行に、名言の出どころとモデルの確信度が表示されます。",
    "shortLead": "{{days}}日間は同じ名言を繰り返さないというルールを守るには、名言が足りません。",
    "shortItem": "{{screen}}：承認済み{{n}}件＝{{n}}日分",
    "shortTail": "それ以降は最も古いものが再び表示されます。追加で公開するか、まとめて生成してください。",
    "noKeyLead": "サーバーにANTHROPIC_API_KEYが設定されていません。",
    "noKeyRest": "新しい名言を生成できず、登録数もすでに不足しています。",
    "curatedLead": "名言は手作業で選定しています。",
    "curatedRest": "公開済みは{{n}}件で、{{days}}日間は繰り返さないというルールを満たすのに十分です。AI生成はオフです。",
    "addKey": "新しい名言を自動作成したい場合は、{{path}}にキーを追加して、バックエンドを再起動してください。",
    "generating": "生成中…",
    "generateNow": "今すぐ生成",
    "modelLabel": "モデル：{{model}}",
    "publishedHome": "公開済み — ホーム",
    "publishedDev": "公開済み — 開発担当",
    "add": "名言を追加",
    "colStatus": "ステータス",
    "colQuote": "名言",
    "colAttribution": "発言者",
    "colShownOn": "表示先",
    "colTimes": "回数",
    "noMatch": "このフィルターに該当する名言はありません。",
    "recent": "最近表示されたもの"
  },
  "form": {
    "required": "名言の本文と発言者は、どちらも必須です。",
    "saveError": "名言を保存できませんでした。",
    "editTitle": "名言 #{{id}} を編集",
    "addTitle": "名言を追加",
    "close": "閉じる",
    "quoteEn": "名言 — English *",
    "quoteVi": "名言 — Tiếng Việt",
    "quoteZh": "名言 — 简体中文",
    "quoteFr": "名言 — Français",
    "quoteJa": "名言 — 日本語",
    "quoteKo": "名言 — 한국어",
    "author": "発言者 *",
    "authorKo": "発言者 — 한국어",
    "authorZh": "発言者 — 简体中文",
    "authorFr": "発言者 — Français",
    "authorJa": "発言者 — 日本語",
    "roleEn": "肩書き / 業績",
    "roleVi": "肩書き — Tiếng Việt",
    "roleKo": "肩書き — 한국어",
    "roleZh": "肩書き — 简体中文",
    "roleFr": "肩書き — Français",
    "roleJa": "肩書き — 日本語",
    "org": "会社 / 組織",
    "orgVi": "組織 — Tiếng Việt",
    "orgZh": "組織 — 简体中文",
    "orgFr": "組織 — Français",
    "orgJa": "組織 — 日本語",
    "orgViPh": "会社名の場合は空欄",
    "orgKo": "組織 — 한국어",
    "birth": "生年",
    "death": "没年（存命の場合は空欄）",
    "shownOn": "表示先",
    "theme": "テーマ",
    "source": "出典 / 発言者の確からしさ",
    "sourcePh": "インタビュー、書籍、または「広く伝えられている」",
    "cancel": "キャンセル",
    "save": "保存",
    "saving": "保存中…"
  }
};

const dictionary = { ns: 'xInsights', en, ko, vi, zh, fr, ja };

export default dictionary;
